<script setup lang="ts">
import SectionHeader from '../common/SectionHeader.vue'
import ActionButton from '../common/ActionButton.vue'
import LastUpdatedDate from './LastUpdatedDate.vue'
import FaqQuestion from './FaqQuestion.vue'
import QuickFactsGrid from './drug-test/QuickFactsGrid.vue'
import StateVariations from './drug-test/StateVariations.vue'
import DrugTestContentSections from './drug-test/DrugTestContentSections.vue'
import DrugTestOverview from './drug-test/DrugTestOverview.vue'
import CompanyTopJobs from './CompanyTopJobs.vue'
interface DrugTestData {
  overview?: string
  quick_facts?: {
    'Type'?: string
    'When'?: string
    'Roles most tested'?: string
  }
  state_variations?: Record<string, string>
  pre_employment?: string
  random_testing?: string
  faq?: Record<string, { Answer: string }>
  employee_experiences?: string
}
const props = withDefaults(defineProps<{
  permalink: string
  brand_color?: string
  drug_test?: DrugTestData
  company_name?: string
  preFetchedData?: any
  logoUrl?: string
}>(), {
  brand_color: '#5F0CE2',
  drug_test: undefined,
  company_name: '',
  preFetchedData: null,
  logoUrl: ''
})
const drugTestData = computed(() => {
  return props.drug_test && typeof props.drug_test === 'object'
    ? props.drug_test
    : null
})
const preFetchedJobsData = computed(() => {
  return props.preFetchedData?.jobsData || { jobs: [], total: 0, available: 0 }
})
const jobsDataPending = computed(() => {
  return !props.preFetchedData?.jobsData
})
const currentYear = new Date().getFullYear()
</script>
<template>
  <div>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
      <SectionHeader :title="`${props.company_name} Drug Test Policy (${currentYear}): What Applicants Should Know`"
        class="mb-6" />
      <div class="flex">
        <LastUpdatedDate section="drug-test-policy" />
      </div>
      <DrugTestOverview :company-name="props.company_name || ''" :overview-text="drugTestData?.overview" />
      <QuickFactsGrid :quick-facts="drugTestData?.quick_facts" :company-name="props.company_name || ''" />
      <StateVariations :state-variations="drugTestData?.state_variations" />
      <DrugTestContentSections :drug-test-content="{
        pre_employment: drugTestData?.pre_employment,
        random_testing: drugTestData?.random_testing,
        employee_experiences: drugTestData?.employee_experiences
      }" :quick-facts="drugTestData?.quick_facts" />
      <SectionHeader title="Frequently Asked Questions" class="mb-6" />
      <div class="bg-white rounded-lg mb-8">
        <div class="space-y-4">
          <FaqQuestion v-for="(answer, question) in drugTestData?.faq" :key="question" :question="question.toString()"
            :answers="answer" :brand-color="props.brand_color" />
        </div>
      </div>
    </div>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 mb-8">
      <SectionHeader :title="`Latest Job Openings at ${props.company_name}`" class="mb-6">
        <template #action>
          <NuxtLink :to="`/company/${props.permalink}/jobs`" class="hidden sm:block">
            <ActionButton text="See all jobs" icon="i-heroicons-arrow-right" size="sm" variant="ghost"
              :brand-color="props.brand_color" />
          </NuxtLink>
        </template>
      </SectionHeader>
      <div class="block sm:hidden mb-4">
        <NuxtLink :to="`/company/${props.permalink}/jobs`">
          <ActionButton text="See all jobs" icon="i-heroicons-arrow-right" size="sm" variant="ghost"
            class="justify-start" :brand-color="props.brand_color" />
        </NuxtLink>
      </div>
      <CompanyTopJobs :permalink="props.permalink" :location="''" :company_name="props.company_name || ''"
        :pre-fetched-data="preFetchedJobsData" :is-loading="jobsDataPending" :logo_url="props.logoUrl" />
    </div>
  </div>
</template>